/**
 * Router — code-first TanStack Router config.
 *
 * Marketing shell (landing, pricing, methodology, sample report), auth pages,
 * and the /app surfaces behind RequireAuth.
 */
import {
  Outlet,
  RootRoute,
  Route,
  Router,
  createRootRoute,
  createRoute,
  createRouter,
} from '@tanstack/react-router';

import { RequireAuth } from '@/components/auth/RequireAuth';
import { AppShell } from '@/components/layout/AppShell';
import { MarketingShell } from '@/components/layout/MarketingShell';
import { HiveCallbackPage } from '@/pages/auth/HiveCallbackPage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { SignupPage } from '@/pages/auth/SignupPage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';
import { AdminAccessRequestsPage } from '@/pages/admin/AdminAccessRequestsPage';
import { AdminOrgDetailPage } from '@/pages/admin/AdminOrgDetailPage';
import { AdminOrgsPage } from '@/pages/admin/AdminOrgsPage';
import { AssetsPage } from '@/pages/app/AssetsPage';
import { AuditDetailPage } from '@/pages/app/AuditDetailPage';
import { AuditsPage } from '@/pages/app/AuditsPage';
import { BillingPage } from '@/pages/app/BillingPage';
import { DashboardPage } from '@/pages/app/DashboardPage';
import { DriftPage } from '@/pages/app/DriftPage';
import { PrChecksPage } from '@/pages/app/PrChecksPage';
import { SettingsApiTokensPage } from '@/pages/app/SettingsApiTokensPage';
import { SettingsIntegrationsPage } from '@/pages/app/SettingsIntegrationsPage';
import { SpecsPage } from '@/pages/app/SpecsPage';
import { UsagePage } from '@/pages/app/UsagePage';
import { LandingPage } from '@/pages/marketing/LandingPage';
import { MethodologyPage } from '@/pages/marketing/MethodologyPage';
import { PricingPage } from '@/pages/marketing/PricingPage';
import { SampleReportPage } from '@/pages/marketing/SampleReportPage';

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// --- Marketing group ---
const marketingLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'marketing',
  component: MarketingShell,
});
const indexRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: '/',
  component: LandingPage,
});
const pricingRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: '/pricing',
  component: PricingPage,
});
const methodologyRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: '/methodology',
  component: MethodologyPage,
});
const sampleReportRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: '/sample-report',
  component: SampleReportPage,
});

// --- Auth group (uses MarketingShell for the header) ---
const loginRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: '/login',
  component: LoginPage,
});
// Hive SSO redirect target (OIDC code+PKCE). Registered even when Hive mode
// is off — the page explains itself if VITE_HIVE_URL is unset.
const authCallbackRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: '/auth/callback',
  component: HiveCallbackPage,
});
const signupRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: '/signup',
  component: SignupPage,
});
const verifyEmailRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: '/verify-email',
  component: VerifyEmailPage,
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});

// --- Authed app group ---
const authGateRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth-gate',
  component: RequireAuth,
});
const appLayoutRoute = createRoute({
  getParentRoute: () => authGateRoute,
  id: 'app-layout',
  component: AppShell,
});
const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app',
  component: DashboardPage,
});

const billingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/billing',
  component: BillingPage,
});

const specsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/specs',
  component: SpecsPage,
});

const auditsListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/audits',
  component: AuditsPage,
});

const auditDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/audits/$auditId',
  component: AuditDetailPage,
});

const settingsIntegrationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/settings/integrations',
  component: SettingsIntegrationsPage,
});

const prChecksRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/pr-checks',
  component: PrChecksPage,
});

const settingsApiTokensRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/settings/api-tokens',
  component: SettingsApiTokensPage,
});

const assetsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/assets',
  component: AssetsPage,
});

const driftRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/drift',
  component: DriftPage,
});

const usageRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/usage',
  component: UsagePage,
});

const adminOrgsRoute = createRoute({
  getParentRoute: () => authGateRoute,
  path: '/_admin/orgs',
  component: AdminOrgsPage,
});

const adminOrgDetailRoute = createRoute({
  getParentRoute: () => authGateRoute,
  path: '/_admin/orgs/$orgId',
  component: AdminOrgDetailPage,
});

const adminAccessRequestsRoute = createRoute({
  getParentRoute: () => authGateRoute,
  path: '/_admin/access-requests',
  component: AdminAccessRequestsPage,
});

// Surfaces still on the post-v1 polish list.
const placeholderAppRoutes = (
  [
    ['/app/repos', 'SDK repos'],
    ['/app/docs-domains', 'Docs domains'],
    ['/app/settings/org', 'Organization settings'],
    ['/app/settings/members', 'Members'],
  ] as const
).map(([path, title]) =>
  createRoute({
    getParentRoute: () => appLayoutRoute,
    path,
    component: () => <PlaceholderPage title={title} scope="app" />,
  }),
);

function PlaceholderPage({ title, scope }: { title: string; scope?: 'app' }) {
  const wrapper = scope === 'app' ? 'mx-auto max-w-6xl px-8 py-10' : 'mx-auto max-w-6xl px-6 py-16';
  return (
    <div className={wrapper}>
      <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
      <p className="mt-2 text-sm text-slate-500">
        This page ships in a later phase. The route exists so navigation is complete.
      </p>
    </div>
  );
}

const routeTree = rootRoute.addChildren([
  marketingLayoutRoute.addChildren([
    indexRoute,
    pricingRoute,
    methodologyRoute,
    sampleReportRoute,
    loginRoute,
    authCallbackRoute,
    signupRoute,
    verifyEmailRoute,
    resetPasswordRoute,
  ]),
  authGateRoute.addChildren([
    appLayoutRoute.addChildren([
      dashboardRoute,
      billingRoute,
      specsRoute,
      auditsListRoute,
      auditDetailRoute,
      settingsIntegrationsRoute,
      settingsApiTokensRoute,
      prChecksRoute,
      assetsRoute,
      driftRoute,
      usageRoute,
      ...placeholderAppRoutes,
    ]),
    adminOrgsRoute,
    adminOrgDetailRoute,
    adminAccessRequestsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Re-export Router type so linter doesn't strip unused imports above.
export type { Router, RootRoute, Route };
